/**
 * A station's board (the station workspaces plan, docs/plans/2026-10-06_station-workspaces.md, step 5): its watch list
 * (step 3, platform-core/internal/twin/stations_watch.go) and its levels (step 4, stations_levels.go), both computed by
 * the server when read. The web renders them and judges nothing itself: a level, its band and its trend arrive in the
 * row's own unit, already the org's display unit.
 *
 *   GET /v1/twin/stations/{station_id}/watch        the station's watch list, its pins, and whether the caller may pin
 *   GET /v1/twin/stations/watch                     the plant-wide list: what no station's equipment carries
 *   PUT /v1/twin/stations/{station_id}/watch/pins   {pins}: the whole set (an org admin, or the station's lead or engineer)
 *   GET /v1/twin/stations/{station_id}/levels       each watched tag's latest value against the plant's window
 *
 * The three reads carry the brief's stakes and defects, so a vendor overlay is refused them (403 vendor_no_twin) and
 * the board hides those panels quietly for it (boardHidden). The levels are read again every 30 s while a gateway feeds
 * the station, every 5 minutes otherwise; the watch lists every 3 minutes.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";
import { useOrg } from "@/lib/org-context";
import { errorCodeOf, isProductRefused, statusOf } from "./error";

/** Why a condition is on a list: a brief's driver, a plant window, or a person's pin. */
export type WatchReason = "driver" | "window" | "pinned";

/** What a condition puts at stake: whole excess pieces; cost and currency only where the org priced a casting. */
export interface WatchStake {
  pieces: number;
  cost?: number;
  currency?: string;
}

/** One watched condition, in the server's order (the most at stake first). `unit` is the org's display unit. */
export interface WatchItem {
  condition_key: string;
  label: string;
  unit: string;
  /** The equipment that carries it: on a station's list, the station's; on the plant-wide list, the plant's. */
  entity_ids: string[];
  reason: WatchReason;
  stake: WatchStake;
  /** A driver's best verdict; absent for a window or a pin. */
  verdict?: "supported" | "suggestive";
  defect?: string;
  defect_label?: string;
  mechanism?: string;
  mechanism_title?: string;
  pinned: "in" | null;
}

/** A pin as stored: who set it and when. A condition pinned out is here and not among the items. */
export interface WatchPin {
  condition_key: string;
  pin: "in" | "out";
  why: string;
  set_by: string | null;
  /** Their display name, else their email; "" when the person has left. */
  set_by_name: string;
  set_at: string;
}

export interface StationWatch {
  station_id: string;
  computed_at: string;
  /** The overall brief's analysis run; null before the plant's first brief. */
  run_id: string | null;
  run_done_at: string | null;
  items: WatchItem[];
  pins: WatchPin[];
  can_pin: boolean;
}

export interface PlantWatch {
  computed_at: string;
  run_id: string | null;
  run_done_at: string | null;
  items: WatchItem[];
}

/** A pin as written: the whole set goes in one PUT, `why` 1 to 280 characters. */
export interface WatchPinPut {
  condition_key: string;
  pin: "in" | "out";
  why: string;
}

/** ok, watch, warn, or stop (outside a stop limit an org admin set on a supported driver: step 4b), or none. */
export type LevelName = "ok" | "watch" | "warn" | "stop" | "none";
export type LevelWhy =
  | "below"
  | "above"
  | "below_stop"
  | "above_stop"
  | "near_edge"
  | "trend"
  | "no_window"
  | "no_value"
  | "no_tag"
  | "unit_mismatch";

/** One tag's level of a watched condition. Value, band and slope are in `unit`, this row's own (the org's display unit). */
export interface StationLevel {
  condition_key: string;
  label: string;
  /** "" when no tag of the station carries the condition (why no_tag). */
  entity_id: string;
  tag_id: string;
  tag_name: string;
  source: "gateway" | "sheet" | "manual" | "computed" | "";
  value: number | null;
  unit: string;
  at: string | null;
  /** time: an observation; day: known only to its day; entry: timed only by when it was typed. */
  at_basis?: "time" | "day" | "entry";
  /** The window, and the stop limit's ends that apply (step 4b; an end inside the window is never sent). */
  band: { lo?: number; hi?: number; grade?: string; stop_lo?: number; stop_hi?: number };
  level: LevelName;
  why?: LevelWhy;
  /** The Sen slope per hour; the minutes until it reaches the edge it heads for count from the response's as_of. */
  trend: { slope_per_h: number; reaches_edge_in_min?: number } | null;
  /** What the condition puts at risk, in the brief's words. */
  at_risk?: string;
}

export interface StationLevels {
  station_id: string;
  as_of: string;
  /** A gateway fed one of its items within the hour; false for a plant on sheets. */
  live: boolean;
  /** The running heat's grade the windows are read for; "" when none is known. */
  grade: string;
  items: StationLevel[];
}

const enc = encodeURIComponent;

export const stationBoardApi = {
  watch: (id: string) => api.get<StationWatch>(`/v1/twin/stations/${enc(id)}/watch`),
  plantWatch: () => api.get<PlantWatch>("/v1/twin/stations/watch"),
  levels: (id: string) => api.get<StationLevels>(`/v1/twin/stations/${enc(id)}/levels`),
  putPins: (id: string, pins: readonly WatchPinPut[]) =>
    api.put<StationWatch>(`/v1/twin/stations/${enc(id)}/watch/pins`, { pins }),
};

/** Under the stations' key, so deleting a station (which reads the whole key again) refreshes its board too. */
export const stationWatchKey = (id: string) => ["twin-stations", "watch", id] as const;
export const PLANT_WATCH_KEY = ["twin-stations", "plant-watch"] as const;
export const stationLevelsKey = (id: string) => ["twin-stations", "levels", id] as const;
/** Every board read's key prefix: a change to a station's equipment or people changes its list, its levels, the
 * plant-wide list and who may pin, so a write to them reads all three again. */
export const STATION_BOARD_PREFIXES = [
  ["twin-stations", "watch"],
  ["twin-stations", "levels"],
  PLANT_WATCH_KEY,
] as const;

/** How often a board's levels are read again: live, as a gateway feeds them; otherwise slowly (a sheet's day). */
export const LEVELS_LIVE_MS = 30_000;
export const LEVELS_IDLE_MS = 300_000;
/** How often a watch list is read again: a new brief, a pin or a change of station moves it, so a board left open on a
 * wall screen keeps its reasons, stakes and `can_pin` current. Slower than the levels, which move by the minute. */
export const WATCH_REFRESH_MS = 180_000;

/** The levels' next read: every 30 s while a gateway feeds the station, every 5 minutes otherwise. */
export function levelsRefetchMs(levels: StationLevels | undefined): number {
  return levels?.live ? LEVELS_LIVE_MS : LEVELS_IDLE_MS;
}

/** True when the board's reads are refused to this caller for good: a vendor overlay reads no brief's stakes, and an org
 *  whose Foundry Twin is not active reads none of the board (the product gate). */
export function boardHidden(err: unknown): boolean {
  return errorCodeOf(err) === "vendor_no_twin" || isProductRefused(err);
}

/** A watch list's next read: every few minutes, never for a caller it is refused to. */
export function watchRefetchMs(err: unknown): number | false {
  return boardHidden(err) ? false : WATCH_REFRESH_MS;
}

/** A failed read is tried again twice, unless the server refused it (a 4xx will refuse it again). */
export function retryBoardRead(failures: number, err: unknown): boolean {
  const status = statusOf(err);
  return failures < 2 && !(status !== undefined && status >= 400 && status < 500);
}

/** The pins were saved, but the server could not read the list again to answer with it (500 pins_saved_read_failed). */
export function pinsSavedUnread(err: unknown): boolean {
  return errorCodeOf(err) === "pins_saved_read_failed";
}

const isLiveApiFlag = import.meta.env.VITE_USE_LIVE_API === "true";

function useBoardLive(): boolean {
  const { isAuthenticated } = useAuth();
  const { activeOrgId } = useOrg();
  return isLiveApiFlag && isAuthenticated && activeOrgId !== null;
}

/** A station's watch list, its pins and whether the caller may set them, read again every few minutes. */
export function useStationWatchQuery(id: string | null | undefined) {
  const live = useBoardLive();
  return useQuery({
    queryKey: stationWatchKey(id ?? ""),
    queryFn: () => stationBoardApi.watch(id ?? ""),
    enabled: live && !!id,
    staleTime: 60_000,
    retry: retryBoardRead,
    refetchInterval: (q) => watchRefetchMs(q.state.error),
  });
}

/** The plant-wide list: the watched conditions no station's equipment carries, read again every few minutes. */
export function usePlantWatchQuery(enabled = true) {
  const live = useBoardLive();
  return useQuery({
    queryKey: PLANT_WATCH_KEY,
    queryFn: stationBoardApi.plantWatch,
    enabled: live && enabled,
    staleTime: 60_000,
    retry: retryBoardRead,
    refetchInterval: (q) => watchRefetchMs(q.state.error),
  });
}

/** A station's levels, read again as often as the station is fed (levelsRefetchMs). */
export function useStationLevelsQuery(id: string | null | undefined) {
  const live = useBoardLive();
  return useQuery({
    queryKey: stationLevelsKey(id ?? ""),
    queryFn: () => stationBoardApi.levels(id ?? ""),
    enabled: live && !!id,
    staleTime: 15_000,
    retry: retryBoardRead,
    refetchInterval: (q) => (boardHidden(q.state.error) ? false : levelsRefetchMs(q.state.data)),
  });
}

/**
 * Set a station's pins, the whole set. The answer is the station's list, and the levels and the plant-wide list follow
 * it. A save the server could not read back (pins_saved_read_failed) was still saved, so the list is read again.
 */
export function usePutWatchPins(id: string) {
  const qc = useQueryClient();
  const readAgain = () => {
    void qc.invalidateQueries({ queryKey: stationLevelsKey(id) });
    void qc.invalidateQueries({ queryKey: PLANT_WATCH_KEY });
  };
  return useMutation({
    mutationFn: (pins: readonly WatchPinPut[]) => stationBoardApi.putPins(id, pins),
    onSuccess: (watch) => {
      qc.setQueryData(stationWatchKey(id), watch);
      readAgain();
    },
    onError: (err) => {
      if (!pinsSavedUnread(err)) return;
      void qc.invalidateQueries({ queryKey: stationWatchKey(id) });
      readAgain();
    },
  });
}
